import {Injectable} from '@angular/core';
import {createState, select, Store, withProps} from '@ngneat/elf';
import {localStorageStrategy, persistState} from '@ngneat/elf-persist-state';
import {Merchant} from '@shared/interfaces/merchant.model';
import {HttpClient} from '@angular/common/http';
import {map, tap} from 'rxjs/operators';
import {environment} from '@env/environment';
import {Observable} from 'rxjs';
import {Router} from '@angular/router';
import {NgxPermissionsService} from 'ngx-permissions';

export interface CredentialState {
    merchant: Merchant | undefined;
}

const {state, config} = createState(withProps<CredentialState>({
    merchant: undefined,
}));

const store = new Store({name: 'credentialStore', state, config});

const getState = () => store.getValue();

const updateState = (newState: Partial<CredentialState>) => {
    store.update(s => ({...s, ...newState}));
};

const persist = persistState(store, {
    key: 'credential',
    storage: localStorageStrategy,
});

/**
 * Provides storage for authentication credentials.
 * The Credentials interface should be replaced with proper implementation.
 */
@Injectable({
    providedIn: 'root'
})
export class CredentialsService {
    name$ = store.pipe(select(state1 => state1.merchant?.userName));

    constructor(private http: HttpClient,
                private router: Router,
                private permissionsService: NgxPermissionsService
    ) {
        const role = store.getValue()?.merchant?.role;
        if (role) {
            this.permissionsService.loadPermissions([role]);
        }
    }

    login(userName: string, password: string): Observable<Merchant> {
        return this.http.post<any>(`${environment.apiUrl}/api/merchant/login`, {userName, password})
            .pipe(
                map(result => result.model),
                tap(merchant => {
                    console.log('Set Role:', merchant.role);
                    this.permissionsService.loadPermissions([merchant.role]);
                    updateState({merchant});
                    localStorage.setItem('token', merchant.token.id);
                })
            );
    }

    setPassword(oldPassword: string, newPassword: string): Observable<void> {
        const merchantId = getState().merchant.id;
        return this.http.post<void>(`${environment.apiUrl}/api/merchant/changePassword`, {
            merchantId,
            oldPassword,
            newPassword
        });
    }

    public logout() {
        localStorage.setItem('token', null);
        store.reset();
        this.router.navigate(['login']);
    }

    isAuthenticated(): boolean {
        return !!getState().merchant && !this.isTokenExpire();
    }

    get token(): string | null {
        return getState().merchant?.token?.id;
    }

    get name(): string | null {
        return getState().merchant?.userName;
    }

    private isTokenExpire() {
        if (new Date().getMilliseconds() < getState().merchant.token.expire) {
            return false;
        } else {
            console.log('Token expire');
            return true;
        }
    }
}
